// Modale « Mode maintenance » : ouvre ou termine une fenêtre de maintenance
// pour un agent OU un site (les deux cibles partagent le même écran, seul le
// libellé change). Tant qu'une fenêtre est active, le serveur suspend la
// création de nouvelles alertes, les notifications et les lignes de journal
// hors ligne/retour en ligne — sans jamais cacher ni résoudre les alertes déjà
// ouvertes (cf. Services/MaintenanceService.cs).

import { useEffect, useState } from 'react';
import { X, Wrench, Clock, Check } from 'lucide-react';
import { api } from '../lib/api.jsx';

const PRESETS = [
  { key: '1h', label: '1 heure', hours: 1 },
  { key: '4h', label: '4 heures', hours: 4 },
  { key: '24h', label: '24 heures', hours: 24 },
  { key: 'manual', label: "Jusqu'à arrêt manuel", hours: null },
];

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

// Valeur par défaut d'un <input type="datetime-local"> : heure LOCALE au
// format sans fuseau, sans quoi le navigateur affiche une heure décalée par
// rapport à ce que l'utilisateur vient de choisir.
const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function MaintenanceModal({ agentId, siteId, label, onClose }) {
  const isSite = !agentId && !!siteId;
  const query = isSite ? `siteId=${encodeURIComponent(siteId)}` : `agentId=${agentId}`;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState('1h');
  const [customEnabled, setCustomEnabled] = useState(false);
  const [customStart, setCustomStart] = useState(() => toLocalInputValue(new Date()));
  const [customEnd, setCustomEnd] = useState(() => toLocalInputValue(new Date(Date.now() + 3600000)));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/platform/maintenance-windows?${query}`).then((r) => {
      setData(r);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, siteId]);

  const create = async () => {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError('Un motif est requis.');
      return;
    }
    setError(null);
    setSaving(true);

    let body;
    if (customEnabled) {
      const startsAt = new Date(customStart);
      const endsAt = new Date(customEnd);
      if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt) {
        setSaving(false);
        setError('La fin doit être postérieure au début.');
        return;
      }
      body = { startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString(), reason: trimmed };
    } else {
      const chosen = PRESETS.find((p) => p.key === preset) || PRESETS[0];
      const endsAt = chosen.hours ? new Date(Date.now() + chosen.hours * 3600000).toISOString() : null;
      body = { endsAt, reason: trimmed };
    }
    body[isSite ? 'siteId' : 'agentId'] = isSite ? siteId : agentId;

    const r = await api.post('/platform/maintenance-windows', body);
    setSaving(false);
    if (r) onClose(true);
    else setError('Échec de la création de la fenêtre de maintenance.');
  };

  const endWindow = async (id) => {
    setSaving(true);
    const r = await api.post(`/platform/maintenance-windows/${id}/end`, {});
    setSaving(false);
    if (r) onClose(true);
    else setError('Échec de la fin de maintenance.');
  };

  const removeUpcoming = async (id) => {
    setSaving(true);
    const ok = await api.del(`/platform/maintenance-windows/${id}`);
    setSaving(false);
    if (ok) load();
    else setError('Échec de la suppression.');
  };

  const active = data?.active;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[440px] max-w-full max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex-none">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Wrench size={18} strokeWidth={1.75} className="text-blue-400" />
              <h2 className="text-base font-bold text-fg">Mode maintenance</h2>
            </div>
            <button
              onClick={() => onClose(false)}
              className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
          <div className="text-[12px] text-muted-2">
            {isSite ? 'Site' : 'Appareil'} :{' '}
            <span className="text-fg-2 font-semibold">{label || (isSite ? siteId : agentId)}</span>
          </div>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="text-muted-2 text-sm py-6 text-center">Chargement…</div>
          ) : active ? (
            <div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 mb-4">
                <div className="text-[13px] font-semibold text-fg-2 mb-1 flex items-center gap-1.5">
                  <Wrench size={14} strokeWidth={1.75} className="text-blue-400" />
                  En maintenance
                </div>
                <div className="text-[12px] text-muted-2 mb-0.5">
                  Jusqu'à : {active.endsAt ? fmtDateTime(active.endsAt) : "arrêt manuel"}
                </div>
                <div className="text-[12px] text-muted-2 mb-0.5">Motif : {active.reason}</div>
                <div className="text-[11px] text-muted-3">
                  Démarrée par {active.createdByEmail || '—'} le {fmtDateTime(active.createdAt)}
                </div>
              </div>
              {error && <div className="text-crit text-[11px] mb-2">{error}</div>}
              <button
                onClick={() => endWindow(active.id)}
                disabled={saving}
                className="w-full bg-rule border border-rule-3 rounded-lg py-2 text-fg-2 cursor-pointer text-xs font-semibold hover:bg-rule-2 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              >
                <Check size={14} strokeWidth={2} /> Terminer la maintenance maintenant
              </button>
            </div>
          ) : (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-2">Durée</div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => {
                      setPreset(p.key);
                      setCustomEnabled(false);
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold border cursor-pointer inline-flex items-center justify-center gap-1.5 ${
                      !customEnabled && preset === p.key
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'bg-sidebar border-rule text-muted hover:bg-rule-2'
                    }`}
                  >
                    <Clock size={12} strokeWidth={1.75} /> {p.label}
                  </button>
                ))}
              </div>

              <label className="flex items-center gap-2 text-[12px] text-muted-2 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customEnabled}
                  onChange={(e) => setCustomEnabled(e.target.checked)}
                />
                Dates explicites
              </label>

              {customEnabled && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <div className="text-[10.5px] text-muted-2 mb-1">Début</div>
                    <input
                      type="datetime-local"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="w-full bg-sidebar border border-rule rounded-lg px-2 py-1.5 text-fg text-xs outline-none"
                    />
                  </div>
                  <div>
                    <div className="text-[10.5px] text-muted-2 mb-1">Fin</div>
                    <input
                      type="datetime-local"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="w-full bg-sidebar border border-rule rounded-lg px-2 py-1.5 text-fg text-xs outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="text-[11px] uppercase tracking-widest text-blue-400 font-bold mb-2 mt-3">
                Motif
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. : redémarrage planifié, migration de serveur…"
                rows={3}
                maxLength={500}
                className="w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none resize-none"
              />

              {data?.upcoming?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-muted-2 font-bold mb-2">
                    Fenêtres à venir
                  </div>
                  {data.upcoming.map((w) => (
                    <div
                      key={w.id}
                      className="flex items-center justify-between gap-2 py-1.5 border-b border-sidebar text-[11px]"
                    >
                      <span className="text-muted-2">
                        {fmtDateTime(w.startsAt)} → {w.endsAt ? fmtDateTime(w.endsAt) : 'arrêt manuel'}
                      </span>
                      <button
                        onClick={() => removeUpcoming(w.id)}
                        disabled={saving}
                        className="text-muted-3 hover:text-crit cursor-pointer bg-transparent border-none text-[11px]"
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && <div className="text-crit text-[11px] mt-3">{error}</div>}

              <button
                onClick={create}
                disabled={saving}
                className="w-full mt-4 bg-blue-500 border-none rounded-lg py-2 text-white cursor-pointer text-xs font-semibold hover:bg-blue-600 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              >
                <Wrench size={14} strokeWidth={1.75} /> {saving ? 'Création…' : 'Démarrer la maintenance'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
